import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Navigate,
  Outlet,
  useNavigate,
  useLocation as useRouterLocation,
  useSearchParams,
} from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Callout, EmptyState } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { useLocation } from '@/shared/hooks/useLocation';
import { lifecycleApi } from '../api/lifecycleApi';
import { strategyKeys } from '../queryKeys';
import { StepNavBar, WIZARD_STEPS } from './StepNavBar';
import { BottomNav } from './BottomNav';
import styles from './StrategyWizardLayout.module.css';

const STEP_PATHS = WIZARD_STEPS.filter((s) => s.id > 0);
const TOTAL_STEPS = STEP_PATHS.length;

function pathToStep(pathname: string): number {
  const segment = pathname.split('/').pop() ?? '';
  const match = STEP_PATHS.find((s) => s.path === segment);
  return match?.id ?? 1;
}

function stepToPath(step: number): string {
  const match = STEP_PATHS.find((s) => s.id === step);
  return match?.path ?? 'profile';
}

function parseVersion(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function StrategyWizardLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routerLocation = useRouterLocation();
  const [searchParams] = useSearchParams();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const currentStep = useMemo(() => pathToStep(routerLocation.pathname), [routerLocation.pathname]);
  const onBeforeNextRef = useRef<(() => void | Promise<void>) | null>(null);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const registerOnBeforeNext = useCallback((fn: (() => void | Promise<void>) | null) => {
    onBeforeNextRef.current = fn;
  }, []);

  const registerNextDisabled = useCallback((disabled: boolean) => {
    setIsNextDisabled(disabled);
  }, []);

  const { data: lifecycle } = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
  });

  // Resolve the target version. Priority: ?version=N URL param → the latest
  // non-archived version, which is the open draft when there is one (else the
  // live version). Defaulting to the live version while a draft was open made
  // edits land on the draft's inputs while the page showed the live version.
  const explicitVersion = parseVersion(searchParams.get('version'));
  const targetVersion = explicitVersion ?? lifecycle?.data?.strategy?.version;
  const targetStrategy =
    targetVersion === lifecycle?.data?.active?.version
      ? lifecycle?.data?.active
      : lifecycle?.data?.strategy;
  const openDraft =
    lifecycle?.data?.strategy && lifecycle.data.strategy.id !== lifecycle.data.active?.id
      ? lifecycle.data.strategy
      : null;
  // Inputs (answers, radius, competitor picks, uploads) are shared by every
  // version, so changes made while viewing the live one change it in place.
  const viewingLive = targetStrategy?.status === 'active';

  const { showToast } = useToast();
  const { mutate: openRefresh, isPending: openingRefresh } = useMutation({
    mutationFn: () => lifecycleApi.openRefresh(locationId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
      navigate(`${routerLocation.pathname}?version=${res.data.version}`);
    },
    onError: (err: unknown) => {
      showToast((err as Error).message || 'Could not start a strategy refresh', 'error');
    },
  });

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [currentStep]);

  // A ?version=N that names an archived (or unknown) version: the backend
  // refuses reads and writes for it, so say so instead of rendering a step
  // that fails on every call.
  const explicitVersionUnavailable =
    explicitVersion !== undefined &&
    !!lifecycle?.data &&
    !lifecycle.data.versions.some((v) => v.version === explicitVersion && v.status !== 'archived');

  const { mutate: syncStep } = useMutation({
    mutationFn: (step: number) => lifecycleApi.updateStep(locationId, step, targetVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
    },
  });

  // The saved step is the furthest step reached; only report forward moves
  // (or the first step of a not-yet-started draft). The backend enforces the
  // same rule, this just avoids pointless writes on Back.
  const maybeSyncStep = useCallback(
    (step: number) => {
      if (!locationId || !targetStrategy || targetStrategy.status === 'active') return;
      const saved = targetStrategy.currentStep ?? 0;
      if (step > saved || targetStrategy.status === 'not_started') syncStep(step);
    },
    [locationId, syncStep, targetStrategy],
  );

  // Preserve ?version=N across step navigation so the user stays anchored on
  // whichever version they opened.
  const withParams = useCallback(
    (path: string) => {
      const params = new URLSearchParams();
      if (explicitVersion) params.set('version', String(explicitVersion));
      const qs = params.toString();
      return qs ? `${path}?${qs}` : path;
    },
    [explicitVersion],
  );

  const goToStep = useCallback(
    (step: number) => {
      if (step === 0) {
        navigate('/strategy');
        return;
      }
      navigate(withParams(`/strategy/builder/${stepToPath(step)}`));
      maybeSyncStep(step);
    },
    [navigate, maybeSyncStep, withParams],
  );

  // Leaving a step by any route (Back, the step bar, Next) saves its pending
  // edits first; before, only Next did, so the others dropped them. A failed
  // save keeps the user on the step (the step reported why).
  const leaveTo = useCallback(
    async (step: number) => {
      if (isNavigating) return;
      setIsNavigating(true);
      try {
        await onBeforeNextRef.current?.();
        goToStep(step);
      } catch {
        // stay
      } finally {
        setIsNavigating(false);
      }
    },
    [goToStep, isNavigating],
  );

  const handleBack = useCallback(() => {
    void leaveTo(currentStep > 1 ? currentStep - 1 : 0);
  }, [currentStep, leaveTo]);

  const handleNext = useCallback(async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      await onBeforeNextRef.current?.();
      if (currentStep < TOTAL_STEPS) goToStep(currentStep + 1);
    } catch {
      // The step's own save surfaced the error; stay on this step.
    } finally {
      setIsNavigating(false);
    }
  }, [currentStep, goToStep, isNavigating]);

  // Steps call this when their content is fully confirmed, without navigating away.
  // Only advances — never moves the saved step backwards (safe when editing from overview).
  const reportStepReached = maybeSyncStep;

  const outletContext = useMemo(
    () => ({
      lifecycle: lifecycle?.data,
      locationId,
      targetVersion,
      goToStep,
      registerOnBeforeNext,
      registerNextDisabled,
      reportStepReached,
    }),
    [
      lifecycle?.data,
      locationId,
      targetVersion,
      goToStep,
      registerOnBeforeNext,
      registerNextDisabled,
      reportStepReached,
    ],
  );

  return (
    <div className={styles.layout}>
      <StepNavBar
        currentStep={currentStep}
        onStepClick={(step) => void leaveTo(step)}
        strategyStatus={targetStrategy?.status}
      />
      <div ref={scrollContainerRef} className={styles.scrollArea}>
        {explicitVersionUnavailable ? (
          <EmptyState
            title={`Version ${explicitVersion} is no longer editable`}
            description="It was archived or replaced. Open the strategy overview to see the current version."
            action={{ label: 'Go to overview', onClick: () => navigate('/strategy') }}
          />
        ) : (
          <>
            {viewingLive && (
              <Callout
                variant="info"
                title="You're viewing your live strategy"
                className={styles.liveNotice}
                actions={
                  openDraft ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        navigate(`${routerLocation.pathname}?version=${openDraft.version}`)
                      }
                    >
                      Edit the refresh draft
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => openRefresh()}
                      loading={openingRefresh}
                      disabled={openingRefresh}
                    >
                      Start a refresh
                    </Button>
                  )
                }
              >
                Changes made here apply to it straight away and can't be undone. To change your
                strategy safely, work in a refresh — the live version stays as it is until you
                approve the new one.
              </Callout>
            )}
            {/* Keyed by location: switching location remounts the step so no
                state from the previous location can be saved onto the new one. */}
            <Outlet key={locationId} context={outletContext} />
          </>
        )}
      </div>
      <BottomNav
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        onBack={handleBack}
        onNext={handleNext}
        nextDisabled={isNextDisabled || isNavigating || explicitVersionUnavailable}
      />
    </div>
  );
}

/** `/strategy/builder` → the first step, keeping any `?version=N`. */
export function StrategyBuilderIndexRedirect() {
  const { search } = useRouterLocation();
  return <Navigate to={`profile${search}`} replace />;
}
